import React from "react";
import Reveal from "@/components/landing/Reveal";
import { CREDENTIALS } from "@/lib/landing-data";

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const CheckIcon = () => (
    <span
        aria-hidden="true"
        className="mt-1 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border border-gold-500/40 bg-gold-50"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#A88643"
            strokeWidth="2"
            className="h-3.5 w-3.5"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12.5l4.5 4.5L19 7"
            />
        </svg>
    </span>
);

const CredentialsList = ({ items }) => (
    <ul className="mt-10 grid gap-5">
        {items.map((item) => (
            <li key={item.id} className="flex items-start gap-4">
                <CheckIcon />
                <span className="font-sans text-base font-medium text-onyx md:text-[17px]">
                    {item.label}
                </span>
            </li>
        ))}
    </ul>
);

const DoctorPlaque = () => (
    <div className="absolute -bottom-6 left-6 right-6 bg-navy px-6 py-5 text-white shadow-luxe md:left-auto md:right-6 md:max-w-[280px]">
        <p className="font-serif text-xl leading-tight text-white md:text-[22px]">
            Dr. André Oliva, PhD
        </p>
        <p className="mt-3 font-sans text-[10px] uppercase tracking-widestx text-gold-400">
            Implantes e Cirurgia Avançada
        </p>
        <p className="mt-3 font-sans text-[11px] tracking-wide text-white/60">
            CROSP 111994
        </p>
    </div>
);

const Portrait = () => (
    <Reveal className="relative lg:col-span-5" y={32} duration={1}>
        <div className="relative">
            {/* Decorative frame */}
            <div
                aria-hidden="true"
                className="absolute -left-4 -top-4 hidden h-full w-full border border-gold-500/70 md:block"
            />
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-ash">
                <img
                    src="/images/dr-portrait.jpg"
                    alt="Retrato profissional do cirurgião-dentista, especialista em implantodontia"
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                />
            </div>
            <DoctorPlaque />
        </div>
    </Reveal>
);

const AuthorityCopy = () => (
    <div className="lg:col-span-7">
        <Reveal>
            <p className="eyebrow flex items-center gap-4">
                <span className="hairline" />A Autoridade Por Trás do
                Procedimento
            </p>
        </Reveal>

        <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-[1.1] text-onyx text-balance sm:text-4xl md:text-5xl lg:text-[52px]">
                Mais de uma década de{" "}
                <span className="italic text-navy">rigor cirúrgico</span>.
            </h2>
        </Reveal>

        <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl font-sans text-base font-light leading-relaxed text-onyx/70 md:text-lg">
                A segurança de um procedimento cirúrgico repousa na
                capacitação técnica de quem o executa. Cada caso é
                conduzido com protocolos rigorosos, planejamento
                individualizado e tecnologia de ponta — do diagnóstico à
                entrega da reabilitação definitiva.
            </p>
        </Reveal>

        <Reveal delay={0.3}>
            <CredentialsList items={CREDENTIALS} />
        </Reveal>

        <Reveal delay={0.45}>
            <div className="mt-12">
                <a
                    href="#tecnologia"
                    data-testid="authority-cta"
                    className="group relative inline-flex items-center gap-3 border-b border-navy pb-1 font-sans text-xs font-semibold uppercase tracking-widestx text-navy transition-colors duration-300 hover:text-gold-600 hover:border-gold-600 md:text-[13px]"
                >
                    <span>Conhecer a Estrutura</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12h14M13 5l7 7-7 7"
                        />
                    </svg>
                </a>
            </div>
        </Reveal>
    </div>
);

/* -------------------------------------------------------------------------- */
/*  Authority                                                                 */
/* -------------------------------------------------------------------------- */

const Authority = () => (
    <section
        id="autoridade"
        data-testid="authority-section"
        className="relative bg-ivory py-24 md:py-32 lg:py-40"
    >
        <div className="container-luxe">
            <div className="grid grid-cols-1 items-center gap-12 md:gap-16 lg:grid-cols-12 lg:gap-20">
                <Portrait />
                <AuthorityCopy />
            </div>
        </div>
    </section>
);

export default Authority;
